(() => {
  const formatMoney = (cents, currency) => {
    const locale = document.documentElement.lang || 'en-US';
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency || 'USD',
      minimumFractionDigits: 2,
    }).format(Number(cents || 0) / 100);
  };

  const moveInstallmentBelowCta = (productInfo) => {
    const installmentForm = productInfo?.querySelector('form.installment');
    const productForm = productInfo?.querySelector('product-form');
    if (!installmentForm || !productForm) return null;

    const sourceWrapper = installmentForm.parentElement;
    installmentForm.classList.add('sp-installment-after-cta');
    productForm.insertAdjacentElement('afterend', installmentForm);

    if (sourceWrapper && sourceWrapper !== productInfo && !sourceWrapper.children.length) {
      sourceWrapper.remove();
    }

    return installmentForm;
  };

  class ShopByCase extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.caseQuantity = Number(this.dataset.caseQuantity || 0);
      this.caseVariantId = this.dataset.caseVariantId || '';
      this.singlePrice = Number(this.dataset.singlePriceCents || 0);
      this.casePrice = Number(this.dataset.casePriceCents || 0);
      this.currency = this.dataset.currency || 'USD';
      this.productInfo = this.closest('product-info') || this.closest('.product');
      this.form = this.productInfo?.querySelector('product-form form[data-type="add-to-cart-form"]');
      this.variantIdInput = this.form?.querySelector('[name="id"]');
      this.singleVariantId = this.variantIdInput?.value || '';
      this.submitButton = this.form?.querySelector('[name="add"]');
      this.submitLabel = this.submitButton?.querySelector('span');
      this.quantityInput = this.productInfo?.querySelector('.product-form__quantity input[name="quantity"]');
      this.quantityLabel = this.productInfo?.querySelector('.product-form__quantity .quantity__label');
      this.quantityCartRule = this.quantityLabel?.querySelector('.quantity__rules-cart');
      this.quantityCartRuleWasHidden = this.quantityCartRule?.hidden || false;
      this.summary = this.querySelector('[data-case-summary]');
      this.error = this.querySelector('[data-case-error]');

      this.installmentForm = moveInstallmentBelowCta(this.productInfo);

      this.querySelectorAll('input[type="radio"]').forEach((radio) => {
        radio.addEventListener('change', () => this.setMode(radio.value));
      });
      this.quantityInput?.addEventListener('input', () => this.updateSubmitLabel());
      this.quantityInput?.addEventListener('change', () => this.updateSubmitLabel());
      this.form?.addEventListener('submit', (event) => this.handleSubmit(event), true);

      this.setMode('single');
    }

    setMode(mode) {
      const nextMode = mode === 'case' ? 'case' : 'single';
      if (nextMode === 'case' && this.mode !== 'case' && this.variantIdInput?.value) {
        this.singleVariantId = this.variantIdInput.value;
      }
      this.mode = nextMode;
      if (this.mode === 'single' && this.variantIdInput && this.singleVariantId) {
        this.variantIdInput.value = this.singleVariantId;
      }
      this.querySelectorAll('[data-purchase-card]').forEach((card) => {
        card.classList.toggle('is-selected', card.dataset.purchaseCard === this.mode);
      });
      if (this.summary) this.summary.hidden = this.mode !== 'case';
      if (this.installmentForm) this.installmentForm.hidden = this.mode === 'case';
      if (this.quantityCartRule) {
        this.quantityCartRule.hidden = this.mode === 'case' ? true : this.quantityCartRuleWasHidden;
      }
      this.updateQuantityLabel();
      this.updateSubmitLabel();
      this.clearError();
    }

    updateQuantityLabel() {
      if (!this.quantityLabel) return;
      let labelText = this.quantityLabel.querySelector('[data-sp-quantity-label]');
      if (!labelText) {
        labelText = this.quantityLabel.querySelector('span[aria-hidden="true"]:not(.quantity__rules-cart)');
        if (labelText) {
          labelText.dataset.spQuantityLabel = '';
        } else {
          const textNode = [...this.quantityLabel.childNodes].find(
            (node) => node.nodeType === Node.TEXT_NODE && node.textContent.trim()
          );
          labelText = document.createElement('span');
          labelText.dataset.spQuantityLabel = '';
          if (textNode) textNode.replaceWith(labelText);
          else this.quantityLabel.prepend(labelText);
        }
      }
      labelText.textContent = this.mode === 'case' ? 'Number of Cases' : 'Quantity';
    }

    updateSubmitLabel() {
      if (!this.submitLabel || this.submitButton?.disabled) return;
      const quantity = Math.max(1, Number(this.quantityInput?.value || 1));
      const unitPrice = this.mode === 'case' ? this.casePrice : this.singlePrice;
      const label = this.mode === 'case' ? 'Add Case to Cart' : 'Add to cart';
      this.submitLabel.textContent = `${label} \u00B7 ${formatMoney(unitPrice * quantity, this.currency)}`;
    }

    handleSubmit(event) {
      if (this.mode !== 'case') return;

      this.clearError();
      if (!this.variantIdInput || !this.caseVariantId) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.showError('This case could not be added to the cart. Please try again.');
        return;
      }

      this.variantIdInput.value = this.caseVariantId;
    }

    showError(message) {
      if (!this.error) return;
      this.error.textContent = message;
      this.error.hidden = false;
    }

    clearError() {
      if (!this.error) return;
      this.error.textContent = '';
      this.error.hidden = true;
    }
  }

  class ShopProductDetails extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.productInfo = this.closest('product-info') || this.closest('.product');
      this.installmentForm = moveInstallmentBelowCta(this.productInfo);

      const source = this.querySelector('[data-details-source]');
      const sourceDescription = source?.querySelector('[data-source-description]');
      const sourceShipping = source?.querySelector('[data-source-shipping]');
      if (!sourceDescription || !sourceShipping) return;

      const description = document.createDocumentFragment();
      const specifications = document.createDocumentFragment();
      let inSpecifications = false;

      [...sourceDescription.children].forEach((child) => {
        const isSpecificationsHeading = /^(specifications|key features|product details):?$/i.test(
          child.textContent.trim()
        );
        if (isSpecificationsHeading) {
          inSpecifications = true;
          return;
        }
        (inSpecifications ? specifications : description).append(child.cloneNode(true));
      });

      this.populate('description', description);
      this.populate('specifications', specifications);
      this.populate('shipping', this.cloneChildren(sourceShipping));
      source.remove();

      this.querySelectorAll('[data-details-tab]').forEach((tab) => {
        tab.addEventListener('click', () => this.activateTab(tab.dataset.detailsTab));
      });
      this.activateTab('description');
    }

    cloneChildren(element) {
      const fragment = document.createDocumentFragment();
      [...element.children].forEach((child) => fragment.append(child.cloneNode(true)));
      return fragment;
    }

    populate(name, fragment) {
      const desktopPanel = this.querySelector(`[data-details-panel="${name}"]`);
      const mobilePanel = this.querySelector(`[data-mobile-panel="${name}"]`);
      if (!desktopPanel || !mobilePanel) return;

      const nodes = [...fragment.childNodes];
      if (!nodes.length) {
        const fallback = document.createElement('p');
        fallback.textContent = name === 'specifications'
          ? 'Product specifications are included in the description above.'
          : 'Details are not available.';
        nodes.push(fallback);
      }
      nodes.forEach((node) => {
        desktopPanel.append(node.cloneNode(true));
        mobilePanel.append(node.cloneNode(true));
      });
    }

    activateTab(name) {
      this.querySelectorAll('[data-details-tab]').forEach((tab) => {
        const active = tab.dataset.detailsTab === name;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', String(active));
      });
      this.querySelectorAll('[data-details-panel]').forEach((panel) => {
        panel.hidden = panel.dataset.detailsPanel !== name;
      });
    }
  }

  if (!customElements.get('shop-by-case')) customElements.define('shop-by-case', ShopByCase);
  if (!customElements.get('shop-product-details')) customElements.define('shop-product-details', ShopProductDetails);
})();
